import {
  ILegalStructureEdge,
  ILegalStructureEntityRow,
  ILegalStructureGraphResponse,
  ILegalStructureNodeRef,
} from '../../../../services/queries/MaggieLegalStructureQueries';
import { nodeKey } from './nodeKey';
import { KNOWN_RELATIONSHIP_TYPES, ROLE_BY_ENTITY_ROLE, toRole } from './nodeMapping';
import { toLegalName } from './nodeTitle';
import { IStructureEdge, IStructureGraph, IStructureNode, RelationshipType } from './types';

/**
 * Outside co-holders of the vehicles a rooted chart already draws (FD-4933).
 *
 * **The problem.** `StructureGraphService.buildGraph` walks a fund or family root
 * *forward* only. Altamont's `ACP BLP Co-Invest, LLC` is a sibling holder of
 * `BL Products Parent, L.P.` — it holds the same holdco the eight ACP funds hold, but it
 * descends from no ACP fund, so a forward walk can never reach it. The chart is not hiding
 * the vehicle; it is never told about it. Same shape of gap as FD-4918, where a fund root's
 * own family was unreachable because membership runs the other way.
 *
 * That matters because these vehicles are exactly the ones with nothing else to find them
 * by. A single-LP co-invest is excluded from reporting by design — the GP holds it as
 * managing member with no economics — so it carries no cost or FMV and appears in no
 * roll-up. The structure is the only place it can show up at all, and someone did put
 * capital in.
 *
 * **The rule**, from the product thread of 2026-08-31: *"anything that has a direct LP only
 * relationship that isn't directly into an ACP fund we want included"*, and *"even if we
 * don't have the metrics, we should show the org flow"*.
 *
 * Two functions, both pure: pick the nodes worth asking about, then fold the answers in.
 * The fetching is `useCoHolders`.
 */

/**
 * The nodes whose holders are worth asking for: the **vehicle tier**.
 *
 * Three exclusions, and the third is the one that carries the product decision.
 *
 * *Families*, because membership is not ownership — nobody holds a family.
 *
 * *Portfolio companies*, for two reasons that agree. A co-invest attaches at the holdco —
 * it subscribes for its own units there — and in this tenant the ledger goes dark below
 * the holdco, so equity into an operating company is not a shape the data produces. Asking
 * about companies would also multiply the request count by the widest tier of the chart,
 * and the reverse question about one company already has the company-up view (FD-4892).
 *
 * *Real funds*, because the rule this exists to serve is Ross's, and it has a second half:
 * *"anything that has a direct LP only relationship **that isn't directly into an ACP
 * fund** we want included"*. Expanding a fund's own holders would put GP entities,
 * management companies and LP commitments above the root card on every chart that has such
 * edges — real structure, but not what was asked for, and a visible change to charts this
 * ticket never mentioned.
 *
 * **Intermediary funds are kept**, and the distinction is the point: `fundKind:
 * 'intermediary'` marks a holding vehicle that carries a fund row to pair with, not a fund
 * anyone commits capital to. `selectRootFunds` already refuses to offer them as roots for
 * the same reason. Excluding every fund-typed node would have missed a co-invest that
 * holds one.
 *
 * Read off the raw payload rather than the mapped graph: the payload carries each node's
 * `ref` and `fundKind` already, and re-deriving a ref by splitting a display node id would
 * make the read depend on an encoding it has no reason to know.
 */
export function selectCoHolderTargets(response: ILegalStructureGraphResponse): ILegalStructureNodeRef[] {
  return response.nodes
    .filter(
      (node) =>
        node.ref.nodeType !== 'family' &&
        node.kind !== 'PORTFOLIO_COMPANY' &&
        !(node.kind === 'FUND' && node.fundKind !== 'intermediary')
    )
    .map((node) => node.ref);
}

/**
 * Builds the card for a holder the rooted walk never saw.
 *
 * Deliberately the same fields `toStructureGraph` sets, from the same register row: a
 * co-holder resolving its role or its chips by a second set of rules would render as a
 * different-looking card for the same entity, which is how two code paths for one concept
 * start to disagree in public.
 *
 * **The role rule mirrors `toStructureGraph` exactly, including its asymmetry**: the
 * register's `role` is taken for *funds*, where it is edge-derived and separates a feeder
 * from a main fund, and every other node type resolves from `kind`. Taking `role` for all
 * of them would agree today only because the api happens to set `role = kind` for company
 * rows; the register's role is documented as edge-derived, so if that ever diverges a
 * merged card would carry a different colour and label than the same entity gets when the
 * walk can reach it — and nothing would fail.
 *
 * The kind comes off the register rather than the graph payload because a co-holder is by
 * definition absent from that payload. That is also why an unregistered holder is dropped
 * rather than drawn.
 */
function toCoHolderNode(id: string, row: ILegalStructureEntityRow): IStructureNode {
  const registerRole = row.ref.nodeType === 'fund' ? ROLE_BY_ENTITY_ROLE[row.role] : null;

  return {
    id,
    name: row.name,
    legalName: toLegalName(row.legalName),
    kind: 'HOLDING_CO',
    role: registerRole ?? toRole({ kind: row.kind, fundKind: row.fundKind }),
    isCoHolder: true,
    legalForm: row.legalForm ?? undefined,
    taxClassification: row.taxClassification ?? undefined,
    jurisdiction: row.jurisdiction ?? undefined,
    debt: row.hostedDebt ?? undefined,
    hostsPec: row.hostsPec ?? undefined,
  };
}

function toCoHolderEdge(edge: ILegalStructureEdge, relationshipType: RelationshipType): IStructureEdge {
  return {
    id: String(edge.id),
    source: nodeKey(edge.parentType, edge.parentId),
    target: nodeKey(edge.childType, edge.childId),
    relationshipType,
    ownershipPercentage: edge.ownershipPercentage,
    votingPercentage: edge.votingPercentage,
    principalAmount: edge.principalAmount,
    interestRate: edge.interestRate,
    effectiveFrom: edge.effectiveFrom,
    effectiveTo: edge.effectiveTo,
    // The DTO's `source` is provenance; `source` on IStructureEdge is the source node.
    provenance: edge.source,
    notes: edge.notes,
    parentRef: { nodeType: edge.parentType, nodeId: edge.parentId },
    childRef: { nodeType: edge.childType, nodeId: edge.childId },
  };
}

/**
 * Folds fetched holder edges into the rooted graph.
 *
 * Call it **before** `withDerivedLanes`: a merged co-holder reaching exactly one portfolio
 * company earns that company's lane on its own, which is where the client's own drawing
 * puts it. Merging afterwards would leave it laneless beside the funds it sits with.
 *
 * Four things are skipped, each for its own reason:
 *
 * - **A holder already in the graph.** `buildGraph` keeps every edge whose ends are both
 *   reachable, so if the parent is in the closure the edge is already drawn. Adding it
 *   again would duplicate a line.
 * - **An unrecognised relationship type**, matching `toStructureGraph`. Drawing one under a
 *   guessed grammar would state something untrue about the relationship. This drops
 *   MEMBERSHIP too, which the chart never draws as a connector.
 * - **A holder in neither the register nor the portfolio list.** There is no name for it,
 *   and an unnamed card is worse than an absent one.
 * - **An edge id already on the graph**, which the two reads can legitimately both carry.
 *
 * Returns the graph unchanged — same reference — when nothing was added, so callers
 * memoising on it do no work on the common path, which is every tenant with no
 * co-investment vehicles.
 */
export function mergeCoHolders(
  graph: IStructureGraph,
  holderEdges: ILegalStructureEdge[],
  register: ILegalStructureEntityRow[],
  companies: { id: number; name: string; legalName?: string | null }[] = []
): IStructureGraph {
  if (holderEdges.length === 0) {
    return graph;
  }

  const presentNodeIds = new Set(graph.nodes.map((node) => node.id));
  const presentEdgeIds = new Set(graph.edges.map((edge) => edge.id));
  const registerByRef = new Map(register.map((row) => [nodeKey(row.ref.nodeType, row.ref.nodeId), row]));
  const companyById = new Map(companies.map((company) => [company.id, company]));

  const addedNodes = new Map<string, IStructureNode>();
  const addedEdges = new Map<string, IStructureEdge>();

  for (const edge of holderEdges) {
    const edgeId = String(edge.id);
    const parentId = nodeKey(edge.parentType, edge.parentId);
    if (presentNodeIds.has(parentId) || presentEdgeIds.has(edgeId) || addedEdges.has(edgeId)) {
      continue;
    }

    const relationshipType = edge.relationshipType as RelationshipType;
    if (!KNOWN_RELATIONSHIP_TYPES.has(relationshipType)) {
      continue;
    }

    const row = registerByRef.get(parentId);
    // A portfolio company holding a vehicle is not a shape this tenant books, but it is a
    // legal edge and the register deliberately holds no companies — so it is named from the
    // portfolio list rather than dropped as unnameable.
    const company = edge.parentType === 'company' ? companyById.get(edge.parentId) : undefined;

    if (row) {
      addedNodes.set(parentId, toCoHolderNode(parentId, row));
    } else if (company) {
      addedNodes.set(parentId, {
        id: parentId,
        name: company.name,
        legalName: toLegalName(company.legalName),
        kind: 'PORTFOLIO_COMPANY',
        role: 'PORTFOLIO_COMPANY',
        isCoHolder: true,
      });
    } else {
      continue;
    }

    addedEdges.set(edgeId, toCoHolderEdge(edge, relationshipType));
  }

  if (addedNodes.size === 0) {
    return graph;
  }

  return {
    ...graph,
    nodes: [...graph.nodes, ...addedNodes.values()],
    edges: [...graph.edges, ...addedEdges.values()],
  };
}
